const { getElement, getElements } = window.BAO.Utils

export class FormValidation extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['message', 'submit']
  }

  setupListeners () {
    super.setupListeners()

    this.requiredEls.forEach((requiredEl) => {
      // Prevent browser default validation
      this.listeners.add(requiredEl, 'invalid', (event) => {
        event.preventDefault()
      })

      // Show validation message on blur
      this.listeners.add(requiredEl, 'blur', (event) => {
        this.toggleValidationMessage(event.target, true)
        this.updateSubmitButtonState()
      })

      // Hide validation message on input and update submit button state
      this.listeners.add(requiredEl, 'input', (event) => {
        this.toggleValidationMessage(event.target, false)
        this.updateSubmitButtonState()
      })

      // Check postcode validation on address autocompletion
      this.listeners.add(document, 'autocomplete', () => {
        const postcodeInput = this.els.postcode.element

        if (postcodeInput) {
          this.toggleValidationMessage(postcodeInput, true)
          this.updateSubmitButtonState()
        }
      })
    })

    // Validate postcode on country change
    if (this?.els?.country?.exists) {
      this.validatePostcodeBasedOnCountry =
        this.validatePostcodeBasedOnCountry.bind(this)

      this.listeners.add(
        this.els.country.element,
        'change',
        this.validatePostcodeBasedOnCountry,
      )
    }

    this.updateSubmitButtonState()
  }

  /**
   * Toggles the validation message for required fields, email, password and postcode
   * @param {HTMLElement} inputEl The input element
   * @param {boolean} isBlur Whether this is triggered by blur
   */
  toggleValidationMessage (inputEl, isBlur) {
    const fieldContainer = inputEl.closest('.frm-Form_Field')
    const messageEl = getElement(this.els.message.selector, {
      context: fieldContainer,
    })

    if (messageEl) {
      const value = inputEl.value.trim()
      const isRequiredInvalid = value === '' // Field is empty
      const isEmailInvalid =
        inputEl.type === 'email' && !this.isValidEmail(value)
      const isRegisterPassword = inputEl.matches(
        '[data-form-validation-el="set-password"]',
      )
      const isPhoneField = inputEl.matches('[data-form-validation-el="phone"]')
      const isPhoneInvalid = isPhoneField && !this.isValidPhone(value)
      const isPasswordInvalid =
        isRegisterPassword && !this.isValidPassword(value)
      const isPostcodeInvalid =
        inputEl.matches('[data-form-validation-el="postcode"]') &&
        !this.isValidPostcode(value)

      let validationMessage = ''

      if (isRequiredInvalid) {
        validationMessage = window.theme.strings.forms.required
      } else if (isEmailInvalid && isBlur) {
        validationMessage = window.theme.strings.forms.invalidEmailAddress
      } else if (isPasswordInvalid && isBlur) {
        validationMessage = window.theme.strings.forms.invalidPassword
      } else if (isPhoneInvalid && isBlur) {
        validationMessage = window.theme.strings.forms.invalidPhone
      } else if (isPostcodeInvalid && isBlur) {
        validationMessage = window.theme.strings.forms.invalidPostcode
      }

      const isInvalid =
        isRequiredInvalid ||
        (isEmailInvalid && isBlur) ||
        (isPasswordInvalid && isBlur) ||
        (isPhoneInvalid && isBlur) ||
        (isPostcodeInvalid && isBlur)
      messageEl.textContent = validationMessage
      messageEl.setAttribute('aria-hidden', isInvalid ? 'false' : 'true')
      inputEl.setAttribute('aria-invalid', isInvalid ? 'true' : 'false')
    }
  }

  /**
   * Checks if an email is valid
   * @param {string} email The email string to validate
   * @returns {boolean} True if valid, false otherwise
   */
  isValidEmail (email) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailPattern.test(email)
  }

  /**
   * Checks if a password is valid
   * @param {string} password The password string to validate
   * @returns {boolean} True if valid, false otherwise
   */
  isValidPassword (password) {
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/
    return passwordPattern.test(password)
  }

  /**
   * Checks if a phone number is valid (allows digits, spaces, +, -, and parentheses)
   * @param {string} phone The phone number string to validate
   * @returns {boolean} True if valid, false otherwise
   */
  isValidPhone (phone) {
    const phonePattern = /^[+0-9\s()-]*$/
    return phonePattern.test(phone)
  }

  /**
   * Checks if a postcode is valid based on the country
   * @param {string} postcode The postcode string to validate
   * @returns {boolean} True if valid, false otherwise
   */
  isValidPostcode (postcode) {
    const country = this.getSelectedCountry()

    let postcodePattern

    switch (country) {
      case 'United Kingdom':
        // UK postcode format: A1 1AA or AA1 1AA (space is optional)
        postcodePattern = /^[A-Za-z]{1,2}\d[A-Za-z\d]? ?\d[A-Za-z]{2}$/
        break
      case 'United States':
        // US zip code format: 12345 or 12345-6789
        postcodePattern = /^\d{5}(-\d{4})?$/
        break
      case 'Canada':
        // Canada postcode format: A1A 1A1
        postcodePattern = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/
        break
      case 'Australia':
        // Australian postcode format: 4000
        postcodePattern = /^\d{4}$/
        break
      case 'Germany':
        // German postcode format: 12345
        postcodePattern = /^\d{5}$/
        break
      case 'France':
        // French postcode format: 75001
        postcodePattern = /^\d{5}$/
        break
      default:
        // For countries without a specific format, allow alphanumeric and space/hyphen
        postcodePattern = /^[A-Za-z0-9\s-]*$/
    }

    return postcodePattern.test(postcode)
  }

  /**
   * Get the selected country from the form
   * @returns {string} The country name
   */
  getSelectedCountry () {
    const countrySelectEl = this.els.country.element
    return countrySelectEl ? countrySelectEl.value : ''
  }

  /**
   * Updates submit button state
   */
  updateSubmitButtonState () {
    const submitButton = this.els.submit.element

    if (!submitButton) return

    const hasInvalidFields = Array.from(this.requiredEls).some((inputEl) => {
      const value = inputEl.value.trim()
      if (inputEl.type === 'email') return !this.isValidEmail(value)
      if (inputEl.matches('[data-form-validation-el="set-password"]')) {
        return !this.isValidPassword(value)
      }
      if (inputEl.matches('[data-form-validation-el="phone"]')) {
        return !this.isValidPhone(value)
      }
      if (inputEl.matches('[data-form-validation-el="postcode"]')) {
        return !this.isValidPostcode(value)
      }
      return value === ''
    })

    submitButton.setAttribute(
      'aria-disabled',
      hasInvalidFields ? 'true' : 'false',
    )
  }

  /**
   * Validates postcode whenever country is selected
   * @param {Event} event
   */
  validatePostcodeBasedOnCountry () {
    const postcodeInput = this.els.postcode.element

    if (postcodeInput) {
      const postcodeValue = postcodeInput.value.trim()
      if (postcodeValue) {
        this.toggleValidationMessage(postcodeInput, true)
      }
    }
  }

  get requiredEls () {
    return getElements("[required], [aria-required='true']", { context: this })
  }
}

if (!customElements.get('form-validation')) {
  customElements.define('form-validation', FormValidation)
}
